import { ChevronLeft, ChevronRight, Gift, Images, Magnet, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { uploadCartTempFiles } from "../../api/CartTempFiles";
import { useCart } from "../../cart/CartContext";
import { createCartItemId } from "../../cart/cartUtils";
import SEO from "../../components/SEO";
import NegativeStripOfferEditor from "../../components/NegativeStripOfferEditor";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import { CATALOG_URL, SITE_URL } from "../../config/site";
import "./OffersPage.css";

const OFFER_PRODUCT_ID = "pack-recuerdos-cine";

const DEFAULT_OFFER = {
  name: "Pack Recuerdos de Cine",
  shortDescription: "Tira de negativos con soporte y tres imanes personalizados incluidos.",
  description:
    "Un pack completo para convertir tres fotos favoritas en una pieza decorativa original, lista para regalar.",
  price: 19.9,
  currency: "EUR",
  image:
    "/images/frame-gallery/frame-gallery-tira-negativos/frame-gallery-tira-negativos-poster.webp",
  gallery: [
    "/images/frame-gallery/frame-gallery-tira-negativos/frame-gallery-tira-negativos-playa.webp",
  ],
  offer: {
    eyebrow: "Oferta destacada",
    requiredPhotos: 3,
    badges: ["3 imanes 5x5 cm", "Tira de negativos", "Soporte incluido"],
    baseProductSlug: "tira-negativos-imanes",
    video: "/videos/tira-negativos-oferta.mp4",
    poster:
      "/images/frame-gallery/frame-gallery-tira-negativos/frame-gallery-tira-negativos-poster.webp",
    editorTitle: "Sube las 3 fotos del pack",
    editorDescription:
      "Ajusta cada foto directamente en la tira de negativos. Estas imágenes se usarán para los tres imanes personalizados del pack.",
  },
};

function formatPrice(value, currency = "EUR") {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency,
  }).format(Number(value) || 0);
}

function getImageAlt(product, index) {
  const name = product?.name || DEFAULT_OFFER.name;

  if (index === 0) {
    return `${name} preparado como regalo personalizado`;
  }

  return `${name} - imagen ${index + 1}`;
}

function mergeOfferProduct(product) {
  return {
    ...DEFAULT_OFFER,
    ...(product || {}),
    offer: {
      ...DEFAULT_OFFER.offer,
      ...(product?.offer || {}),
    },
  };
}

export default function OffersPage() {
  const { addItem } = useCart();
  const [selectedOfferImage, setSelectedOfferImage] = useState(0);
  const [offerProduct, setOfferProduct] = useState(null);
  const [cartMessage, setCartMessage] = useState("");
  const [isAddingOffer, setIsAddingOffer] = useState(false);

  const offer = useMemo(() => mergeOfferProduct(offerProduct), [offerProduct]);
  const offerConfig = offer.offer || {};
  const offerGallery = Array.isArray(offer.gallery) && offer.gallery.length > 0
    ? offer.gallery
    : DEFAULT_OFFER.gallery;
  const activeOfferImage = offerGallery[selectedOfferImage] || offerGallery[0];
  const requiredPhotos = Number(offerConfig.requiredPhotos) || 3;
  const offerPrice = formatPrice(offer.price, offer.currency || "EUR");
  const baseProductSlug = offerConfig.baseProductSlug || "tira-negativos-imanes";
  const baseProductUrl = `/producto/${baseProductSlug}`;

  useEffect(() => {
    let isMounted = true;

    async function loadOfferProduct() {
      try {
        const response = await fetch(CATALOG_URL);
        const data = await response.json();
        const foundProduct = data.products?.find(
          (item) => item.id === OFFER_PRODUCT_ID || item.slug === OFFER_PRODUCT_ID
        );

        if (isMounted) {
          setOfferProduct(foundProduct || null);
        }
      } catch (error) {
        console.error("Error loading offer product", error);
      }
    }

    loadOfferProduct();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setSelectedOfferImage(0);
  }, [offerGallery.length]);

  useEffect(() => {
    if (!cartMessage || isAddingOffer) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setCartMessage("");
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [cartMessage, isAddingOffer]);

  const showPreviousOfferImage = () => {
    setSelectedOfferImage((current) =>
      current === 0 ? offerGallery.length - 1 : current - 1
    );
  };

  const showNextOfferImage = () => {
    setSelectedOfferImage((current) =>
      current === offerGallery.length - 1 ? 0 : current + 1
    );
  };

  const handleOfferSubmit = async (photos) => {
    if (!offerProduct) {
      setCartMessage("No se ha encontrado la oferta en el catálogo.");
      return;
    }

    if (photos.length !== requiredPhotos) {
      setCartMessage(`Completa las ${requiredPhotos} fotos del pack antes de continuar.`);
      return;
    }

    const cartItemId = createCartItemId(offerProduct.id);
    const files = photos.map((photo) => photo.file);

    setIsAddingOffer(true);
    setCartMessage("Preparando las fotos del pack...");

    try {
      const tempFiles = await uploadCartTempFiles({ cartItemId, files });

      addItem({
        cartItemId,
        productId: offerProduct.id,
        productName: offerProduct.name,
        category: offerProduct.category,
        type: offerProduct.type,
        quantity: 1,
        unitPrice: offerProduct.price || 0,
        currency: offerProduct.currency || "EUR",
        options: {
          photos: {
            label: "Fotos",
            value: String(requiredPhotos),
            valueLabel: `${requiredPhotos} fotos`,
          },
          pack: {
            label: "Pack",
            value: offerProduct.slug || offerProduct.id,
            valueLabel: offerProduct.name,
          },
        },
        assets: photos.map((photo, index) => ({
          kind: "uploaded_image",
          role: "negative_strip_pack_photo",
          fileName: photo.name || `foto_${index + 1}.jpg`,
          includedInOrder: true,
        })),
        tempFiles,
        notes: [
          `Pack oferta: ${offerProduct.name}.`,
        ],
        promotion: {
          id: offerProduct.id,
          label: `Oferta ${offerProduct.name}`,
        },
      });

      setCartMessage("Pack añadido al carrito.");
    } catch (error) {
      console.error("Error adding offer to cart", error);
      setCartMessage("No se pudieron preparar las imágenes. Inténtalo de nuevo.");
    } finally {
      setIsAddingOffer(false);
    }
  };

  return (
    <>
      <SEO
        title={`${offer.name} | Ofertas Imalandia`}
        description={offer.shortDescription || offer.description}
        canonical={`${SITE_URL}/ofertas`}
        image={offer.image}
      />

      <Header />

      <main className="offers-page">
        <section className="offers-hero">
          <div className="offers-hero__content">
            <p className="offers-eyebrow">{offerConfig.eyebrow}</p>

            <h1>{offer.name}</h1>

            <p className="offers-hero__text">
              {offer.shortDescription || offer.description}
            </p>

            <div className="offers-hero__badges" aria-label="Incluye">
              {(offerConfig.badges || []).map((badge) => (
                <span key={badge}>{badge}</span>
              ))}
            </div>

            <p className="offers-price">Pack completo: <strong>{offerPrice}</strong></p>

            <div className="offers-actions">
              <a href="#oferta-editor" className="offers-primary-button">
                Personalizar pack
              </a>

              <Link
                to={baseProductUrl}
                className="offers-secondary-button"
              >
                Ver producto base
              </Link>
            </div>
          </div>

          <div className="offers-hero__media">
            <img src={offerGallery[0]} alt={getImageAlt(offer, 0)} />
          </div>
        </section>

        <div id="oferta-editor">
          <NegativeStripOfferEditor
            title={offerConfig.editorTitle}
            description={offerConfig.editorDescription}
            submitLabel={isAddingOffer ? "Preparando..." : "Añadir pack al carrito"}
            onSubmit={handleOfferSubmit}
          />
        </div>

        {cartMessage && (
          <p className="offers-cart-message">
            {cartMessage}{" "}
            {cartMessage.includes("carrito") && <Link to="/carrito">Ver carrito</Link>}
          </p>
        )}

        {offerConfig.video && (
          <section className="offers-video" aria-labelledby="offers-video-title">
            <div className="offers-section-heading">
              <p>Producto real</p>
              <h2 id="offers-video-title">Así queda el pack completo</h2>
            </div>

            <video
              src={offerConfig.video}
              poster={offerConfig.poster || offer.image}
              controls
              muted
              playsInline
              preload="metadata"
            />
          </section>
        )}

        <section className="offers-included" aria-labelledby="included-title">
          <div className="offers-section-heading">
            <p>Todo en un pack</p>
            <h2 id="included-title">Listo para regalar o decorar</h2>
          </div>

          <div className="offers-included__grid">
            <article>
              <Images aria-hidden="true" />
              <h3>{requiredPhotos} fotos protagonistas</h3>
              <p>
                Elige tus momentos favoritos y conviértelos en una pequeña
                historia visual.
              </p>
            </article>

            <article>
              <Magnet aria-hidden="true" />
              <h3>Imanes personalizados</h3>
              <p>
                Incluye imanes de 5x5 cm fabricados con nuestro acabado
                protector.
              </p>
            </article>

            <article>
              <Gift aria-hidden="true" />
              <h3>Soporte incluido</h3>
              <p>
                Para colocarlo en una mesa, estantería o rincón especial sin
                depender de una superficie metálica.
              </p>
            </article>
          </div>
        </section>

        <section className="offers-showcase" aria-labelledby="showcase-title">
          <div className="offers-showcase__copy">
            <p>Idea de regalo</p>
            <h2 id="showcase-title">Pequeño, personal y con mucha historia</h2>
            <span>
              {offer.description}
            </span>
          </div>

          <div className="offers-gallery" aria-label="Imágenes del pack">
            <div className="offers-gallery__stage">
              <img
                src={activeOfferImage}
                alt={getImageAlt(offer, selectedOfferImage)}
                loading="lazy"
              />

              {offerGallery.length > 1 && (
                <>
                  <button
                    type="button"
                    className="offers-gallery__arrow offers-gallery__arrow--prev"
                    onClick={showPreviousOfferImage}
                    aria-label="Ver imagen anterior"
                  >
                    <ChevronLeft aria-hidden="true" />
                  </button>

                  <button
                    type="button"
                    className="offers-gallery__arrow offers-gallery__arrow--next"
                    onClick={showNextOfferImage}
                    aria-label="Ver imagen siguiente"
                  >
                    <ChevronRight aria-hidden="true" />
                  </button>
                </>
              )}
            </div>

            {offerGallery.length > 1 && (
              <div className="offers-gallery__thumbs" aria-label="Seleccionar imagen">
                {offerGallery.map((image, index) => (
                  <button
                    key={image}
                    type="button"
                    className={index === selectedOfferImage ? "is-active" : ""}
                    onClick={() => setSelectedOfferImage(index)}
                    aria-label={`Ver imagen ${index + 1}`}
                  >
                    <img src={image} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="offers-next-step">
          <Sparkles aria-hidden="true" />
          <h2>Un regalo completo, listo para emocionar</h2>
          <p>
            Sube tus fotos favoritas y convierte tus recuerdos en una pieza
            decorativa diferente, personal y fácil de regalar.
          </p>
          <div className="offers-actions offers-actions--center">
            <a href="#oferta-editor" className="offers-primary-button">
              Preparar mi pack por {offerPrice}
            </a>
            <a
              href="https://wa.me/34646273415"
              className="offers-secondary-button"
              target="_blank"
              rel="noreferrer"
            >
              Consultar por WhatsApp
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
